import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRight, FileText, Loader2 } from 'lucide-react';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '../components/ui/command';
import { Popover, PopoverAnchor, PopoverContent } from '../components/ui/popover';
import { resolveMenuIcon } from '../icons/menuIcons';
import { useNavigationTree } from '../modules/useNavigationTree';
import { Highlight } from './Highlight';
import { MIN_QUERY, matchPages, searchPageHash } from './searchModel';
import { useSearch } from './useSearch';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Search field in the header. The field itself is the search: results open
 * right below it, grouped by module. ⌘K / Ctrl+K puts the focus in the field.
 */
export function HeaderSearch() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const tree = useNavigationTree();
  const active = query.trim().length >= MIN_QUERY;
  const { groups, isFetching, debouncedQuery } = useSearch(open ? query : '');
  const pages = useMemo(() => matchPages(tree, query), [tree, query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const close = () => {
    setOpen(false);
    inputRef.current?.blur();
  };
  const go = (path: string) => {
    close();
    setQuery('');
    navigate(path);
  };

  const settled = debouncedQuery === query.trim() && !isFetching;
  const nothing = settled && pages.length === 0 && groups.length === 0;

  return (
    <Command shouldFilter={false} loop label="Suche" className="w-full max-w-md focus-within:max-w-xl transition-[max-width] duration-200">
      <Popover open={open && active} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} className="relative">
            <CommandInput
              ref={inputRef}
              value={query}
              onValueChange={v => { setQuery(v); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={e => { if (e.key === 'Escape') close(); }}
              placeholder="Suchen …"
              aria-label="Suche"
              wrapperClassName="h-10 rounded-full border border-gray-200 bg-gray-50 px-4 transition-colors focus-within:border-primary focus-within:bg-white focus-within:ring-2 focus-within:ring-primary/20"
              className="pr-14"
            />
            <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded-sm border border-gray-200 bg-white px-1.5 text-[11px] text-gray-400">
              {isMac ? '⌘K' : 'Strg K'}
            </kbd>
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
          onOpenAutoFocus={e => e.preventDefault()}
          onInteractOutside={e => { if (anchorRef.current?.contains(e.target as Node)) e.preventDefault(); }}
        >
          <CommandList>
            {pages.length > 0 && (
              <CommandGroup heading="Seiten">
                {pages.map(page => (
                  <CommandItem key={page.path} value={`page:${page.path}`} onSelect={() => go(page.path)}>
                    <FileText className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    <span className="flex-1"><Highlight text={page.label} query={query} /></span>
                    {page.parent && <span className="text-xs text-gray-400">{page.parent}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {groups.map(group => {
              const Icon = resolveMenuIcon(group.icon);
              return (
                <CommandGroup
                  key={group.provider}
                  heading={<><Icon className="h-3.5 w-3.5" aria-hidden="true" />{group.label} · {group.hits.length}{group.more ? '+' : ''}</>}
                >
                  {group.hits.map(hit => (
                    <CommandItem key={hit.id} value={`${group.provider}:${hit.id}`} onSelect={() => go(hit.path)}>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium"><Highlight text={hit.title} query={query} /></span>
                        {(hit.subtitle || hit.snippet) && (
                          <span className="block truncate text-xs text-gray-500"><Highlight text={hit.snippet ?? hit.subtitle!} query={query} /></span>
                        )}
                      </span>
                      {hit.badge && <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">{hit.badge}</span>}
                    </CommandItem>
                  ))}
                  {group.more && (
                    <CommandItem value={`more:${group.provider}`} onSelect={() => go(`/suche${searchPageHash(query, group.provider)}`)} className="text-primary">
                      <span className="flex-1 font-medium">Alle in {group.label}</span>
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </CommandItem>
                  )}
                </CommandGroup>
              );
            })}
            {nothing && (
              <p className="px-4 py-5 text-sm text-gray-500">
                Nichts gefunden für „{query.trim()}“. Kürzer suchen oder die Schreibweise prüfen.
              </p>
            )}
            {!settled && groups.length === 0 && pages.length === 0 && (
              <p className="flex items-center gap-2 px-4 py-5 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Suche läuft …</p>
            )}
            {/* Only once results are in: while loading it would be the sole item, and Enter would open it instead of the first hit */}
            {settled && (
              <>
                <CommandSeparator />
                <CommandItem value="all" onSelect={() => go(`/suche${searchPageHash(query)}`)} className="py-2.5 text-primary">
                  <span className="flex-1 font-medium">Alle Ergebnisse für „{query.trim()}“</span>
                  <span className="hidden gap-3 text-[11px] text-gray-400 sm:flex"><span>↑↓ wählen</span><span>↵ öffnen</span><span>Esc schließen</span></span>
                </CommandItem>
              </>
            )}
          </CommandList>
        </PopoverContent>
      </Popover>
    </Command>
  );
}
